<template>
  <div class="capsule-home-wrapper">
    <router-link
      tag="div"
      class="capsule-sidebar"
      :to="{ name: 'ShowCapsule', params: { capsuleId: capsule.id } }"
      @click.native="trackEvent('Clicked Sidebar')"
    >
      <div class="sidebar-content">
        <div class="avatar">
          <b-img
            rounded="circle"
            :src="capsule.stylist_avatar_url"
            alt="avatar"
            class="avatar1"
          />
        </div>

        <div class="stylist-details hide-sm">
          <div class="username">{{ capsule.stylist_username }}</div>
          <div class="published-date">
            {{ formatDate(capsule.published_on) }}
          </div>
        </div>

        <div class="title-details">
          <div class="title">{{ capsule.title }}</div>
          <div class="occasion">{{ capsule.occasion }}</div>
        </div>

        <div class="capsule-stats">
          <div class="stats-sm d-block d-sm-none">
            <div class="stats">
              <div class="stack">
                <span class="stats-number">{{ capsule.products_count }}</span>
                <span class="stack-title">items</span>
              </div>
              <div class="stack">
                <span class="stats-number">{{ capsule.outfits_count }}</span>
                <span class="stack-title">outfits</span>
              </div>
            </div>
          </div>
          <div class="stats-lg d-none d-sm-block">
            <div class="stats">
              <div class="stack">
                <div class="stack-number">{{ capsule.products_count }}</div>
                <div class="stack-title">Items</div>
              </div>

              <div class="stack">
                <div class="stack-number">{{ capsule.outfits_count }}</div>
                <div class="stack-title">Outfits</div>
              </div>
            </div>
          </div>
        </div>

        <div class="capsule-notes hide-sm">
          <p>{{ capsule.notes }}</p>
        </div>
      </div>
    </router-link>
    <div class="capsule-body">
      <div class="capsule-body-header">
        <router-link
          tag="a"
          class="open-capsule"
          :to="{ name: 'ShowCapsule', params: { capsuleId: capsule.id } }"
          @click.native="trackEvent('Clicked Open')"
        >
          <span data-v-step="3" class="open-btn-wrapper">
            <span class="text">Open</span>
            <!-- <span class="text">Open</span><i class="pe-7s-expand1 icon"></i> -->
          </span>
        </router-link>
        <div class="vote-wrapper">
          <div class="vote-stack" :class="{ 'has-vote': hasVote }">
            <a class="vote-link" v-on:click.prevent.stop="submitVote()">
              <span class="vote-icon"><i class="pe-7f-like2"></i></span>
            </a>
            <span class="vote-count" :class="{ 'no-votes': zeroVotes }">
              {{ zeroVotes ? "vote" : capsule.votes_count }}
            </span>
          </div>
        </div>
      </div>
      <div class="capsule-body-content">
        <CapsuleContent :capsule="capsule" />
      </div>
    </div>
  </div>
</template>

<script>
import CapsuleContent from "@/components/capsule/CapsuleContent";
import CapsuleUtils from "@/utils/CapsuleUtils";
import { mapActions, mapGetters } from "vuex";
import { bus } from "@/main";

export default {
  created() {
    this.formatDate = CapsuleUtils.formatDate;
  },

  components: {
    CapsuleContent,
  },

  props: {
    capsule: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters(["allVotes", "currentUser"]),

    hasVote() {
      return this.allVotes.includes(this.capsule.id);
    },

    zeroVotes() {
      return !(this.capsule.votes_count > 0);
    },
  },

  methods: {
    ...mapActions(["createVote", "destroyVote"]),

    submitVote() {
      if (this.currentUser !== null) {
        this.hasVote
          ? this.destroyVote(this.capsule.id)
          : this.createVote(this.capsule.id);
      } else {
        bus.$emit("openSignUpModal");
      }
    },

    trackEvent(actionName) {
      window.analytics.track(`Discover Page - Open Capsule: ${actionName}`, {
        capsule_id: this.capsule.id,
        stylist_username: this.capsule.stylist_username,
        occasion: this.capsule.occasion,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/_colour-palette.scss";
@import "~@/styles/_breakpoints.scss";

.capsule-home-wrapper {
  // overflow: visible;
  display: flex;
  flex-direction: column;
  align-content: stretch;
  margin-bottom: 70px;
  background-color: $primary-blue;
  border-radius: 0.5em;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);

  margin-right: -15px;
  margin-left: -15px;

  @include for-tablet-portrait-up {
    flex-direction: row;

    margin-right: 0;
    margin-left: 0;
  }
}

// SIDEBAR
.capsule-home-wrapper > .capsule-sidebar {
  color: white;
  padding: 15px;

  @include for-tablet-portrait-up {
    width: 300px;
  }

  &:hover {
    cursor: pointer;
    background-color: lighten($primary-blue, 5%);
  }
}

.capsule-home-wrapper > .capsule-sidebar > .sidebar-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 15px 15px;
  grid-template-areas:
    "avatar title"
    "avatar stats";

  @include for-tablet-portrait-up {
    grid-template-columns: 55px auto;
    grid-template-areas:
      "avatar stylist"
      "title title"
      "stats stats"
      "notes notes";
  }

  .show-sm {
    display: inherit !important;
    @include for-tablet-portrait-up {
      display: none !important;
    }
  }

  .hide-sm {
    display: none !important;
    @include for-tablet-portrait-up {
      display: inherit !important;
    }
  }
}

// SIDEBAR - Avatar
.capsule-home-wrapper > .capsule-sidebar > .sidebar-content > .avatar {
  grid-area: avatar;

  img {
    width: 55px;
    height: 55px;
  }
}

// SIDEBAR - Stylist details
.capsule-home-wrapper > .capsule-sidebar > .sidebar-content > .stylist-details {
  display: table;
  grid-area: stylist;
  margin: auto 0;

  .username {
    letter-spacing: 1.4px;
    font-size: 12px;
    font-weight: 400;
    vertical-align: middle;
    padding-bottom: 3px;
  }

  .published-date {
    vertical-align: middle;
    font-size: 11px;
    font-weight: 400;
    color: $grey3;
  }
}

// SIDEBAR - Title details
.capsule-home-wrapper > .capsule-sidebar > .sidebar-content > .title-details {
  grid-area: title;

  @include for-tablet-portrait-up {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  .title {
    font-size: 26px;
    line-height: 32px;
  }

  .occasion {
    letter-spacing: 1.4px;
    line-height: 24px;
    font-weight: 400;
    font-size: 11px;
    text-transform: uppercase;
    color: $primary-accent;
  }
}

// SIDEBAR - Stats
.capsule-home-wrapper > .capsule-sidebar > .sidebar-content > .capsule-stats {
  grid-area: stats;

  .stats {
    display: flex;
    justify-content: flex-start;
    flex-direction: row;

    .stack {
      text-align: center;

      &:not(:last-child) {
        margin-right: 30px;
      }
      .stack-title {
        color: $grey3;
        text-transform: uppercase;
        letter-spacing: 1.4px;
        font-size: 11px;
        font-weight: 400;
      }
    }
  }

  .stats-sm {
    line-height: 20px;

    .stats-number {
      font-weight: 400;
      font-size: 14px;
      margin-right: 3px;
    }

    .stack-title {
    }
  }

  .stats-lg {
    .stack-number {
      color: $primary-accent;
      font-size: 20px;
      line-height: 32px;
    }

    .stack-title {
      line-height: 24px;
    }
  }
}

// SIDEBAR - Notes
.capsule-home-wrapper > .capsule-sidebar > .sidebar-content > .capsule-notes {
  grid-area: notes;
  font-size: 12px;
}

// BODY
.capsule-home-wrapper > .capsule-body {
  background-color: $grey8;
  flex-grow: 1;
  width: 100%;
  padding-top: 15px;
  padding-bottom: 30px;
  // padding: 15px 15px 30px 15px;

  // @include for-tablet-portrait-up {
  //   padding: 15px 30px 30px 30px;
  // }

  .capsule-body-content {
    padding-left: 15px;
    padding-right: 15px;

    @include for-tablet-portrait-up {
      padding-left: 30px;
      padding-right: 30px;
    }
  }
  .capsule-body-header {
    display: flex;
    padding-left: 15px;
    padding-right: 20px;

    .vote-wrapper {
      text-align: center;
      // padding-right: 10px;
      padding-right: 0px;
      padding-left: 10px;
    }

    .vote-wrapper > .vote-stack {
      display: flex;
      justify-content: flex-start;
      flex-direction: column;
    }

    .vote-wrapper > .vote-stack > .vote-link {
      padding-right: 8px;
      padding-left: 8px;
      cursor: pointer;
      text-align: center;

      &:hover {
        border-radius: 3px;
        background: $grey5;

        .vote-icon {
          color: $mint-bright;
        }
      }
    }

    .vote-wrapper > .vote-stack:not(.has-vote) > .vote-link:hover > .vote-icon {
      opacity: 0.5;
    }

    .vote-wrapper > .vote-stack.has-vote > .vote-link:hover > .vote-icon {
      opacity: 1;
    }

    .vote-wrapper > .vote-stack > .vote-link > .vote-icon {
      color: $grey3;
      font-size: 20px;
      line-height: 32px;
      opacity: 1;
    }

    .vote-wrapper > .vote-stack > .vote-count {
      color: $grey3;
      text-transform: uppercase;
      letter-spacing: 1.4px;
      font-size: 11px;
      font-weight: 400;
      margin-bottom: 5px;

      &.no-votes {
        font-weight: 300;
      }
    }

    .vote-wrapper > .vote-stack.has-vote > .vote-count,
    .vote-wrapper > .vote-stack.has-vote > .vote-link > .vote-icon {
      color: $mint-bright;
      font-weight: 600;
    }

    .open-capsule {
      flex-grow: 1;
      text-align: left;
      padding-left: 15px;
      cursor: pointer;
      text-decoration: none;

      &:hover {
        .text {
          color: darken($grey3, 15%);
        }

        .icon {
          color: darken($mint-bright, 15%);
        }
      }

      .open-btn-wrapper {
        vertical-align: middle;
      }

      .text {
        margin-top: -3px;
        margin-right: 10px;
        letter-spacing: 1.4px;
        font-size: 12px;
        font-weight: 400;
        text-transform: uppercase;
        color: $grey3;
      }

      .icon {
        color: $mint-bright;
        vertical-align: middle;
        font-weight: bold;
      }
    }
  }
}

.v-tour__target--highlighted {
  box-shadow: 0 0 0 99999px rgba(0, 0, 0, 0.4);
}
</style>



// WEBPACK FOOTER //
// src/components/capsule/CapsuleHomeWrapper.vue